// Dedicated PostHog Analytics Module
// Separate from DatabaseAPI - handles all client-side analytics

class Analytics {
    constructor() {
        this.initialized = false;
        this.instance = null;
        this.apiKey = null;
        this.apiHost = null;
        this.userIdentified = false; // CRITICAL: Block all events until user identified
    }

    initialize() {
        if (typeof window !== 'undefined' && !this.initialized) {
            this.apiKey = process.env.REACT_APP_POSTHOG_API_KEY;
            this.apiHost = process.env.REACT_APP_POSTHOG_HOST;
            
            console.log('🎯 Analytics: Initializing PostHog with environment variables');
            console.log('🎯 Analytics: API Key present:', !!this.apiKey);
            console.log('🎯 Analytics: API Host:', this.apiHost);
            
            if (this.apiKey && this.apiHost) {
                this.loadPostHog();
                this.initialized = true;
                
                // Initialize session tracking
                this.initializeSessionTracking();
                
                console.log('✅ Analytics: PostHog initialized successfully with session tracking');
            } else {
                console.warn('⚠️ Analytics: PostHog not initialized - missing environment variables');
                console.warn('⚠️ Analytics: Check REACT_APP_POSTHOG_API_KEY and REACT_APP_POSTHOG_HOST');
                return false;
            }
        }
        return this.initialized;
    }
    
    initializeSessionTracking() {
        // DON'T track session_started here - wait for user identification
        // Just set up event listeners and session storage
        if (!sessionStorage.getItem('analytics_session_started')) {
            sessionStorage.setItem('analytics_session_started', 'true');
            sessionStorage.setItem('analytics_session_start_time', Date.now().toString());
            sessionStorage.setItem('analytics_pages_visited', '0');
        }
        
        // Track page visibility changes for session management
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    // User switched away - could be end of session
                    this.handleSessionPause();
                } else {
                    // User came back - resume session
                    this.handleSessionResume();
                }
            });
            
            // Track page unload for session end
            window.addEventListener('beforeunload', () => {
                this.handleSessionEnd('page_unload');
            });
        }
    }
    
    handleSessionPause() {
        // Store pause time for session tracking
        sessionStorage.setItem('analytics_session_paused', Date.now().toString());
    }
    
    handleSessionResume() {
        // Check if session was paused for too long (30 minutes = new session)
        const pausedTime = sessionStorage.getItem('analytics_session_paused');
        if (pausedTime) {
            const pauseDuration = Date.now() - parseInt(pausedTime);
            const thirtyMinutes = 30 * 60 * 1000;
            
            if (pauseDuration > thirtyMinutes) {
                // Start new session
                this.handleSessionEnd('timeout');
                this.trackSessionStarted('resume_after_timeout');
                sessionStorage.setItem('analytics_session_started', 'true');
                sessionStorage.setItem('analytics_session_start_time', Date.now().toString());
                sessionStorage.setItem('analytics_pages_visited', '0');
            }
            
            sessionStorage.removeItem('analytics_session_paused');
        }
    }
    
    handleSessionEnd(reason = 'unknown') {
        const sessionStartTime = sessionStorage.getItem('analytics_session_start_time');
        const pagesVisited = sessionStorage.getItem('analytics_pages_visited');
        
        if (sessionStartTime) {
            const sessionDuration = (Date.now() - parseInt(sessionStartTime)) / 1000;
            this.trackSessionEnded(sessionDuration, parseInt(pagesVisited) || 0, reason);
        }
        
        // Clear session tracking
        sessionStorage.removeItem('analytics_session_started');
        sessionStorage.removeItem('analytics_session_start_time');
        sessionStorage.removeItem('analytics_pages_visited');
        sessionStorage.removeItem('analytics_session_paused');
        sessionStorage.removeItem('analytics_session_user_identified');
    }

    loadPostHog() {
        // FIXED: Load PostHog with CDN script to avoid infinite loop
        console.log('🎯 Analytics: Loading PostHog via CDN script');
        
        // Create script element
        const script = document.createElement('script');
        script.src = `${this.apiHost.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/array.js`;
        script.async = true;
        
        // Initialize PostHog when script loads
        script.onload = () => {
            console.log('🎯 Analytics: PostHog script loaded, initializing...');
            
            // Initialize PostHog with FIXED configuration
            window.posthog.init(this.apiKey, {
                api_host: this.apiHost,
                person_profiles: 'identified_only',
                // DISABLE ALL AUTOCAPTURE to prevent infinite loops
                autocapture: false,
                capture_pageview: false,
                capture_pageleave: false,
                session_recording: {
                    maskAllInputs: true,
                    maskInputOptions: { password: true }
                },
                // Prevent any automatic captures that could cause loops
                opt_out_capturing: false,
                loaded: (posthog) => {
                    console.log('🎯 Analytics: PostHog fully initialized');
                    this.instance = posthog;
                }
            });
            
            // Fallback assignment
            if (!this.instance) {
                this.instance = window.posthog;
            }
        };
        
        script.onerror = () => {
            console.error('❌ Analytics: PostHog script failed to load');
        };
        
        // Append script to head
        document.head.appendChild(script);
        
        // Initialize empty PostHog stub to prevent errors
        if (!window.posthog) {
            window.posthog = {
                init: () => {},
                identify: () => {},
                capture: () => {},
                reset: () => {}
            };
        }
    }

    // USER IDENTIFICATION
    identifyUser(userId, userProperties = {}) {
        if (!this.initialize()) return false;
        
        try {
            // Use integer user ID as distinct_id (never email)
            const distinctId = String(userId); // Convert to string for PostHog
            
            const properties = {
                ...userProperties,
                last_seen_at: new Date().toISOString()
            };
            
            // CRITICAL: Call identify() to link all future events to Django User.id
            this.instance.identify(distinctId, properties);
            this.userIdentified = true; // Allow events now
            
            console.log(`✅ Analytics: User identified successfully - Django User.id ${distinctId} is PostHog distinct_id`);
            
            // NOW track session_started with correct user ID (after identification)
            const sessionAlreadyStarted = sessionStorage.getItem('analytics_session_user_identified');
            if (!sessionAlreadyStarted) {
                this.trackSessionStarted('app_router');
                sessionStorage.setItem('analytics_session_user_identified', 'true');
                console.log(`🎯 Analytics: session_started tracked with Django User.id - ${distinctId}`);
            }
            
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error identifying user:', error);
            return false;
        }
    }

    resetAnalytics() {
        if (!this.initialize()) return false;
        
        try {
            // End current session before reset
            this.handleSessionEnd('logout');
            
            // Reset PostHog instance and user identification
            this.instance.reset();
            this.userIdentified = false; // Block events again until re-identification
            console.log('🎯 Analytics: Analytics reset (user logged out) - events now blocked until re-identification');
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error resetting analytics:', error);
            return false;
        }
    }

    // NAVIGATION EVENTS
    trackPageView(pageName, previousPage = null, navigationMethod = 'click') {
        if (!this.initialize()) return false;
        
        // CRITICAL: Block events until user is identified to prevent UUID assignment
        if (!this.userIdentified) {
            console.warn(`⚠️ Analytics: page_view blocked - user not identified yet (prevents UUID assignment)`);
            return false;
        }
        
        try {
            const properties = {
                page_name: pageName,
                source: 'client',
                navigation_method: navigationMethod,
                url: window.location.href,
                path: window.location.pathname,
                timestamp: new Date().toISOString()
            };
            
            if (previousPage) {
                properties.previous_page = previousPage;
            }
            
            // Increment pages visited counter
            const currentCount = parseInt(sessionStorage.getItem('analytics_pages_visited') || '0');
            sessionStorage.setItem('analytics_pages_visited', (currentCount + 1).toString());
            properties.session_page_count = currentCount + 1;
            
            this.instance.capture('page_view', properties);
            console.log(`🎯 Analytics: page_view tracked - ${pageName} (page ${currentCount + 1} this session)`);
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error tracking page_view:', error);
            return false;
        }
    }

    trackNavigationAction(action, from, to, method = 'click') {
        if (!this.initialize()) return false;
        
        // CRITICAL: Block events until user is identified
        if (!this.userIdentified) {
            console.warn(`⚠️ Analytics: navigation_action blocked - user not identified yet`);
            return false;
        }
        
        try {
            const properties = {
                navigation_action: action,
                from_page: from,
                to_page: to,
                method: method,
                source: 'client',
                timestamp: new Date().toISOString()
            };
            
            this.instance.capture('navigation_action', properties);
            console.log(`🎯 Analytics: navigation_action tracked - ${action}: ${from} → ${to}`);
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error tracking navigation_action:', error);
            return false;
        }
    }

    // UI INTERACTION EVENTS
    trackButtonClick(buttonName, pageName, additionalData = {}) {
        if (!this.initialize()) return false;
        
        // CRITICAL: Block events until user is identified
        if (!this.userIdentified) {
            console.warn(`⚠️ Analytics: button_click blocked - user not identified yet`);
            return false;
        }
        
        try {
            const properties = {
                button_name: buttonName,
                page_name: pageName,
                source: 'client',
                timestamp: new Date().toISOString(),
                ...additionalData
            };
            
            this.instance.capture('button_click', properties);
            console.log(`🎯 Analytics: button_click tracked - ${buttonName} on ${pageName}`);
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error tracking button_click:', error);
            return false;
        }
    }

    // LEAGUE EVENTS
    trackLeagueUIAction(action, leagueType = null, additionalData = {}) {
        if (!this.initialize()) return false;
        
        // CRITICAL: Block events until user is identified
        if (!this.userIdentified) {
            console.warn(`⚠️ Analytics: league_ui_action blocked - user not identified yet`);
            return false;
        }
        
        try {
            const properties = {
                league_ui_action: action,
                source: 'client',
                timestamp: new Date().toISOString(),
                ...additionalData
            };
            
            if (leagueType) {
                properties.league_type = leagueType;
            }
            
            this.instance.capture('league_ui_action', properties);
            console.log(`🎯 Analytics: league_ui_action tracked - ${action}`);
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error tracking league_ui_action:', error);
            return false;
        }
    }

    trackTeamInteraction(interaction, teamId, context = 'unknown', additionalData = {}) {
        if (!this.initialize()) return false;
        
        // CRITICAL: Block events until user is identified
        if (!this.userIdentified) {
            console.warn(`⚠️ Analytics: team_interaction blocked - user not identified yet`);
            return false;
        }
        
        try {
            const properties = {
                team_interaction: interaction,
                team_id: teamId,
                context: context,
                source: 'client',
                timestamp: new Date().toISOString(),
                ...additionalData
            };
            
            this.instance.capture('team_interaction', properties);
            console.log(`🎯 Analytics: team_interaction tracked - ${interaction} for team ${teamId}`);
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error tracking team_interaction:', error);
            return false;
        }
    }

    // AUTHENTICATION EVENTS
    trackAuthUIAction(action, step = null, method = null) {
        if (!this.initialize()) return false;
        
        // CRITICAL: Block events until user is identified
        if (!this.userIdentified) {
            console.warn(`⚠️ Analytics: auth_ui_action blocked - user not identified yet`);
            return false;
        }
        
        try {
            const properties = {
                auth_ui_action: action,
                source: 'client',
                timestamp: new Date().toISOString()
            };
            
            if (step) {
                properties.auth_step = step;
            }
            
            if (method) {
                properties.auth_method = method;
            }
            
            this.instance.capture('auth_ui_action', properties);
            console.log(`🎯 Analytics: auth_ui_action tracked - ${action}`);
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error tracking auth_ui_action:', error);
            return false;
        }
    }

    // ERROR TRACKING
    trackError(errorType, errorMessage, context = {}) {
        if (!this.initialize()) return false;
        
        // CRITICAL: Block events until user is identified
        if (!this.userIdentified) {
            console.warn(`⚠️ Analytics: error_occurred blocked - user not identified yet`);
            return false;
        }
        
        try {
            const properties = {
                error_type: errorType,
                error_message: errorMessage,
                source: 'client',
                url: window.location.href,
                user_agent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                ...context
            };
            
            this.instance.capture('error_occurred', properties);
            console.log(`🎯 Analytics: error_occurred tracked - ${errorType}: ${errorMessage}`);
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error tracking error_occurred:', error);
            return false;
        }
    }

    // SESSION TRACKING
    trackSessionStarted(entryPoint = 'unknown') {
        if (!this.initialize()) return false;
        
        // CRITICAL: Block events until user is identified (except this is called FROM identifyUser)
        if (!this.userIdentified) {
            console.warn(`⚠️ Analytics: session_started blocked - user not identified yet`);
            return false;
        }
        
        try {
            const properties = {
                entry_point: entryPoint,
                source: 'client',
                user_agent: navigator.userAgent,
                screen_width: window.screen.width,
                screen_height: window.screen.height,
                viewport_width: window.innerWidth,
                viewport_height: window.innerHeight,
                timestamp: new Date().toISOString()
            };
            
            // Track session_started (main event for DAU calculation)
            this.instance.capture('session_started', properties);
            
            console.log('🎯 Analytics: session_started tracked');
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error tracking session_started:', error);
            return false;
        }
    }

    trackSessionEnded(durationSeconds, pagesVisited = 0, exitPoint = 'unknown') {
        if (!this.initialize()) return false;
        
        // CRITICAL: Block events until user is identified
        if (!this.userIdentified) {
            console.warn(`⚠️ Analytics: session_ended blocked - user not identified yet`);
            return false;
        }
        
        try {
            const properties = {
                duration_seconds: durationSeconds,
                pages_visited: pagesVisited,
                exit_point: exitPoint,
                source: 'client',
                timestamp: new Date().toISOString()
            };
            
            this.instance.capture('session_ended', properties);
            console.log('🎯 Analytics: session_ended tracked');
            return true;
        } catch (error) {
            console.error('❌ Analytics: Error tracking session_ended:', error);
            return false;
        }
    }
}

// Export singleton instance
const analytics = new Analytics();
export default analytics;